import { SvgPathBuilder } from '../svg/SvgPathBuilder';
import type { NonEmptyArray } from '../types/NonEmptyArray';
import type { Point } from './point';
import { segment } from './segment';

/**
 * Polygon representation by unclosed line
 *
 * Don't repeat start point at the end, it is assumed
 * that start and end point forms the closing edge
 */
export type Polygon = NonEmptyArray<Point>;

export function create(...points: NonEmptyArray<Point>): Polygon {
  return points;
}

/**
 * Inspired by https://github.com/substack/point-in-polygon/blob/master/index.js
 * This also checks polygon boundaries to be consistent with other hitarea calculators
 */
export function contains(pg: Polygon, pnt: Point): boolean {
  let inside = false;
  const { x, y } = pnt;

  for (let i = 0, j = pg.length - 1; i < pg.length; j = i++) {
    const a = pg[i];
    const b = pg[j];
    if (a === undefined || b === undefined) {
      throw new Error(
        `Unexpected error, Polygon is missing points: ${JSON.stringify(pg)}`,
      );
    }

    if (segment.contains(segment.create(a, b), pnt)) {
      return true;
    }
    if (
      a.y > y !== b.y > y &&
      x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x
    ) {
      inside = !inside;
    }
  }

  return inside;
}

export function getD(p: Polygon, compact = false): string {
  const builder = new SvgPathBuilder(compact).moveTo(p[0]);
  p.slice(1).forEach(builder.lineTo);
  return builder.buildClosed();
}

export const polygon = {
  create,

  contains,
  getD,
};
